import { produce, type Draft, nothing, freeze } from "immer";
import {
  useState,
  useReducer,
  useCallback,
  useMemo,
  type Dispatch
} from "react";

// https://immerjs.github.io/immer/
// https://github.com/immerjs/immer
// https://github.com/immerjs/use-immer

export type DraftFunction<S> = (draft: Draft<S>) => void;
export type Updater<S> = (arg: S | DraftFunction<S>) => void;
export type ImmerHook<S> = [S, Updater<S>];

export function useImmer<S = any>(initialValue: S | (() => S)): ImmerHook<S>;

export function useImmer(initialValue: any) {
  const [val, updateValue] = useState(() =>
    freeze(
      typeof initialValue === "function" ? initialValue() : initialValue,
      true
    )
  );
  return [
    val,
    useCallback((updater: Updater<any>) => {
      if (typeof updater === "function") {
        updateValue(produce(updater));
      } else {
        updateValue(freeze(updater));
      }
    }, [])
  ];
}

export type ImmerReducer<S, A> = (
  draft: Draft<S>,
  action: A
) => S | void | typeof nothing;

export function useImmerReducer<S, A, I>(
  reducer: ImmerReducer<S, A>,
  initializerArg: S & I,
  initializer?: (arg: S & I) => S
): [S, Dispatch<A>] {
  const cachedReducer = useMemo<(state: S, action: A) => S>(
    () => produce(reducer) as (state: S, action: A) => S,
    [reducer]
  );
  return useReducer(cachedReducer, initializerArg as any, initializer as any);
}
